import { useEffect, useState } from 'react';
import api from '@/lib/api';

/**
 * Cache local de mensagens já obtidas para evitar refetch quando o
 * mesmo `id_negocio` aparece em múltiplos componentes (ex: kanban + ficha).
 */
const _cache = new Map();
const _inflight = new Map();

/**
 * React hook que devolve a mensagem WhatsApp pronta a usar para uma lead.
 *
 * @param {number|string} idNegocio — ID da lead (negócio).
 * @param {string} fallback — mensagem default quando o ID não está disponível
 *   ou o lookup falha. Aceita `{Nome}` que é substituído pelo `firstName`.
 * @param {string} firstName — primeiro nome para substituição no fallback.
 * @returns {string} mensagem renderizada (sem URL-encoding).
 */
export default function useWhatsAppMessage(idNegocio, fallback = '', firstName = '') {
  const [msg, setMsg] = useState(() => {
    if (idNegocio && _cache.has(idNegocio)) return _cache.get(idNegocio);
    return fallback.replace(/\{Nome\}/gi, firstName);
  });

  useEffect(() => {
    if (!idNegocio) return;
    if (_cache.has(idNegocio)) {
      setMsg(_cache.get(idNegocio));
      return;
    }
    if (_inflight.has(idNegocio)) {
      _inflight.get(idNegocio).then(setMsg).catch(() => {});
      return;
    }
    const p = api.get(`/whatsapp-templates/lookup/${idNegocio}`)
      .then(r => {
        const m = r?.data?.message || fallback.replace(/\{Nome\}/gi, firstName);
        _cache.set(idNegocio, m);
        return m;
      })
      .catch(() => fallback.replace(/\{Nome\}/gi, firstName))
      .finally(() => _inflight.delete(idNegocio));
    _inflight.set(idNegocio, p);
    p.then(setMsg);
  }, [idNegocio, fallback, firstName]);

  return msg;
}
